import { z } from 'zod';
import type { AiMode } from '../constants';
import type { StateResponse } from './setup/states';

// ---------------------------------------------------------------------------
// Chat Conversations
// ---------------------------------------------------------------------------

// 'chat_task' is an internal combined-fetch value — /chat and /task share a
// single cache keyed on this; the response carries both hub_type values and
// the frontend filters client-side. Keeping it in the enum keeps typed
// clients (Hono RPC, MCP, CLI) aware of the shape.
export const getConversationsQuery = z.object({
  nav_item: z.enum(['chat', 'task', 'support', 'chat_task']),
  hub_team_user_id: z.string().optional(),
  hub_user_id: z.string().optional(),
  search_filter: z.string().optional(),
  hub_id: z.string().uuid().optional(),
  conversation_id: z.string().uuid().optional(),
  // 'ended' = recently-closed, served warm from the HubDO index (≤ retention
  // window). 'archived' = the full closed-conversation history from ClickHouse.
  status: z.enum(['active', 'agent', 'team', 'ended', 'archived']).optional(),
  page: z.string().regex(/^\d+$/).optional(),
  page_size: z.string().regex(/^\d+$/).optional(),
  // Cursor for the continuous-history scroll (chat hubs): with status='archived',
  // return only conversations that ended strictly before this instant. The caller
  // passes the oldest warm-index `ended_at` so the CH superset excludes the
  // already-loaded warm slice. UTC ISO-8601 (Timestamps standard) — datetime()
  // hardens it before it reaches the parameterized CH predicate.
  before_ended_at: z.string().datetime().optional(),
});

// ---------------------------------------------------------------------------
// Chat Messages
// ---------------------------------------------------------------------------

export const getMessagesQuery = z.object({
  hub_id: z.string().uuid(),
  hub_team_user_id: z.string().optional(),
  hub_user_id: z.string().optional(),
  conversation_id: z.string().uuid().optional(),
  message_id: z.string().uuid().optional(),
  page: z.string().regex(/^\d+$/).optional(),
  page_size: z.string().regex(/^\d+$/).optional(),
  /**
   * When true AND the caller has support access (hub admin or hub team member),
   * internal messages (tool calls, tool results) are included in the response.
   * Silently ignored for non-support callers — it's a view preference, not an
   * authorization boundary.
   */
  include_internal: z
    .union([z.literal('true'), z.literal('false'), z.boolean()])
    .optional()
    .transform((v) => v === true || v === 'true'),
});

// ---------------------------------------------------------------------------
// Inferred types
// ---------------------------------------------------------------------------

export type GetConversationsQuery = z.infer<typeof getConversationsQuery>;
export type GetMessagesQuery = z.infer<typeof getMessagesQuery>;

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

/** Team reference in a conversation */
export interface ConversationTeamRef {
  hub_team_id: string;
  team_name: string;
}

/** Agent reference in a conversation */
export interface ConversationAgentRef {
  agent_id: string;
  agent_name: string;
}

/** Browsable resource reference in a conversation */
export interface ConversationBrowsableResource {
  resource_id: string;
  resource_name: string;
  resource_description?: string;
}

/** Kanban status configuration item */
export interface ConversationKanbanStatus {
  slug: string;
  name: string;
  color: string;
  isTerminalStatus?: boolean;
  allowed_next_statuses?: string[];
  [key: string]: unknown;
}

/** Task hub available in metadata */
export interface AvailableTaskHub {
  hub_id: string;
  hub_name: string;
  app_channel_id: string | null;
  hub_environment?: 'preview' | 'production';
}

// Derived server-side from the viewer's user_id vs the conversation's claimer.
export type ClaimStatus = 'unclaimed' | 'claimed_by_this_user' | 'claimed_by_other_user';

/** The nav slice a conversation belongs to for a given viewer. */
export type SourceNav = 'chat' | 'task' | 'support';

/** Single conversation item returned by the get_conversations RPC */
export interface ConversationItemResponse {
  hub_id: string;
  user_id?: string;
  hub_team_user_id?: string;
  conversation_id: string;
  user_name?: string;
  hub_name?: string;
  hub_type?: 'task' | 'chat';
  hub_description?: string;
  hub_contexts?: string[];
  hub_environment?: 'preview' | 'production';
  is_support_hub?: boolean;
  kanban_statuses?: ConversationKanbanStatus[];
  project_name?: string;
  message_text?: string;
  message_status?: 'pending' | 'sent' | 'delivered' | 'read' | 'failed' | null;
  created_at?: string;
  sender_type?: string;
  receiver_type?: string;
  conversation_status?: string;
  /** Channel type of the last message — may be `app` for a team reply. */
  channel_type?: string;
  /** The conversation's bound customer channel (whatsapp/instagram/...) — distinct from `channel_type`. Feeds the support composer's 24h messaging-window gate. */
  current_channel_type?: string;
  /** Timestamp of the last customer (user) inbound message — feeds the 24h messaging-window gate. */
  last_inbound_at?: string;
  app_channel_id?: string;
  search_text?: string;
  sender_description?: string;
  receiver_description?: string;
  channel_description?: string;
  current_hub_team_id?: string;
  current_agent_id?: string;
  current_team_user_id?: string;
  current_responder_type?: string;
  /** Per-contact channel access: 'approved' | 'pending' | 'blocked'. Drives the support badge + composer gating. */
  access_status?: string;
  available_teams: ConversationTeamRef[];
  available_agents: ConversationAgentRef[];
  unread_message_count?: number;
  claim_status?: ClaimStatus;
  kanban_status?: string;
  ended_at?: string;
  // Set when conversation has been archived to R2. Null when ended_at is set
  // but archival hasn't completed (still in DO buffer). The frontend uses this
  // to decide between the archive endpoint and the active /messages endpoint.
  archived_at?: string | null;
  // Evaluator variables
  user_sentiment?: string;
  goal_achieved?: string;
  conversation_summary?: string;
  browsable_resources?: ConversationBrowsableResource[];
  is_flagged?: boolean;
  flag_source?: FlagSource;
  // Nav slices the row belongs to for this viewer. A conversation can belong to
  // more than one (e.g. a viewer who is both the end user AND a team member of a
  // hub sees it in BOTH the owner nav and /support — the roles are orthogonal).
  // The backend emits the row into each matching slice; per-slice it carries the
  // single-element set for that slice, and the frontend unions them by id.
  source_navs?: SourceNav[];
}

/** Source of a conversation flag. Set by evaluator or monitor background agents. */
export type FlagSource = 'evaluator' | 'monitor' | 'both' | null;

/**
 * Merge an incoming flag source with an existing one.
 * Null/same-source → incoming; different existing source → 'both'.
 * Used by ConversationDO and HubDO to keep flag state consistent when the
 * same conversation is flagged by both evaluator and monitor.
 */
export function mergeFlagSource(
  existing: FlagSource,
  incoming: 'evaluator' | 'monitor',
): Exclude<FlagSource, null> {
  if (existing === null || existing === incoming) return incoming;
  return 'both';
}

/** Hub metadata included in the conversations response */
export interface HubItemResponse {
  hub_id: string;
  hub_name?: string;
  hub_type?: 'chat' | 'task';
  hub_description?: string;
  hub_environment?: 'preview' | 'production';
  /** Server-owned disambiguator for sibling previews (see `wayai relabel`). Absent on production hubs. */
  preview_label?: string;
  is_support_hub?: boolean;
  /** Routing/responder policy. Drives UI affordances like "Transfer to Agent". */
  ai_mode?: AiMode;
  /** Hub's IANA timezone — the lens the UI renders message timestamps in. */
  timezone?: string;
  /** Hub language (en|pt|es) — localizes the pending-access notice + backend-sent text. */
  language?: string;
  /** Who may approve/block pending contacts: 'admin' (default) or 'team'. Drives the approve/block UI gating. */
  access_approval_role?: 'admin' | 'team';
  /** Per-hub override for the pending-access notice text (null ⇒ localized default). */
  access_request_message?: string;
  app_channel_id?: string;
  kanban_statuses?: Array<{ slug: string; name: string; color: string; allowed_next_statuses?: string[]; isTerminalStatus?: boolean; lane_slug?: string }>;
  /** Presentational lane definitions for board grouping/filtering (see kanban.ts laneSchema). */
  lanes?: Array<{ slug: string; name: string; color?: string; order?: number }>;
  available_teams: Array<{ hub_team_id: string; team_name: string }>;
  available_agents?: Array<{ agent_id: string; agent_name: string }>;
  /** Enabled team users (id = platform user_id) for resolving "Claimed by {name}". */
  available_team_users?: Array<{ id: string; name: string; team_id: string }>;
  /**
   * Whether the snapshot's user is an enabled hub admin on this hub. Drives
   * admin-only UI affordances (e.g. destructive deletes). Backend remains the
   * authoritative gate — this is a UI-visibility hint, not an auth decision.
   */
  is_current_user_admin?: boolean;
  // Pre-seeds Settings → States tab so it renders without a network round-trip.
  // Optional: omitted by older Worker versions; new clients fall back to a fetch.
  states?: StateResponse[];
}

/** RPC response from get_conversations (returned as JSON from the RPC) */
export interface GetConversationsRpcResponse {
  conversations: ConversationItemResponse[];
  hubs?: HubItemResponse[];
  metadata: {
    available_task_hubs?: AvailableTaskHub[];
    /** Pagination fields (present when status=ended) */
    total?: number;
    page?: number;
    page_size?: number;
  };
  /**
   * True when ClickHouse was cold and the response is a provisional empty
   * result — the client should keep polling until this flag disappears.
   */
  cold?: boolean;
}

/** File attachment in a message */
export interface MessageFileResponse {
  file_id?: string;
  bucket?: string;
  mime_type: string;
  file_name: string;
  file_short_name?: string;
  mime_category?: string;
  file_path: string;
  file_signed_url?: string;
  file_type?: string;
  file_size?: number;
}

/**
 * Diagnostic codes surfaced when AI message processing fails due to hub
 * misconfiguration. Each code has i18n copy (title + action) rendered in the
 * support view to guide the admin to a fix. `internal_error` is the
 * unknown-failure fallback and is the only code that logs at `error` level and
 * reaches Sentry.
 */
export type DiagnosticCode =
  | 'missing_pilot_connection'
  | 'hub_config_not_found'
  | 'credential_resolution_failed'
  | 'missing_api_key'
  | 'llm_provider_rejected'
  | 'rate_limit_exhausted'
  | 'internal_error'
  // A harness (external agent-loop) turn failed after retries and degraded to the
  // team fail-safe (harness-agents PR 2.10a).
  | 'harness_degraded';

export const DIAGNOSTIC_CODES: readonly DiagnosticCode[] = [
  'missing_pilot_connection',
  'hub_config_not_found',
  'credential_resolution_failed',
  'missing_api_key',
  'llm_provider_rejected',
  'rate_limit_exhausted',
  'internal_error',
  'harness_degraded',
] as const;

/** Metadata shape for support-only error diagnostic messages (sender=system, receiver=team). */
export interface MessageErrorMetadata {
  is_error: true;
  error_code: DiagnosticCode;
  args?: Record<string, string>;
}

/** Single message item returned by the get_messages RPC */
export interface MessageItemResponse {
  message_key: string;
  message_id: string;
  conversation_id: string;
  message_text: string;
  message_status?: 'pending' | 'sent' | 'delivered' | 'read' | 'failed' | null;
  sender_type: string;
  receiver_type: string;
  created_at: string;
  conversation_created_at?: string;
  hub_type?: string;
  channel_type?: string;
  channel_id?: string;
  is_last_message?: boolean;
  files: MessageFileResponse[];
  sender_description?: string;
  receiver_description?: string;
  channel_description?: string;
  conversation_start?: boolean;
  metadata?: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------
// ConversationDO composite read: ChatView
// ---------------------------------------------------------------------------

/**
 * Raw file row as stored in ConversationDO's `conversation_file` table,
 * returned attached to the owning message inside `ChatView.messages`.
 * Shape mirrors the SQLite row so the route layer can map it to
 * `MessageFileResponse` without additional DO round-trips.
 */
export interface ChatViewFile {
  file_id: string;
  message_id: string;
  /** Only populated by `getFiles` (archival read); omitted when the row is
   *  joined onto a message in `getMessages` / `getChatView` to keep payloads small. */
  conversation_id?: string | null;
  file_name: string | null;
  file_path: string | null;
  bucket: string | null;
  mime_type: string | null;
  file_size: number | null;
  created_at: string | null;
}

/**
 * Raw message row plus any files joined by `message_id`.
 * `files` is always populated — an empty array when the message has no
 * attachments. This removes the `files=[]` fallback at route level because
 * the DO now joins the two tables in a single SQL transaction.
 */
export interface ChatViewMessage {
  message_id: string;
  conversation_id: string | null;
  message_text: string | null;
  message_type: string | null;
  sender_type: string | null;
  sender_id: string | null;
  sender_name: string | null;
  receiver_type: string | null;
  receiver_id: string | null;
  channel_type: string | null;
  channel_message_sid: string | null;
  keep_in_history: number | boolean | null;
  agent_id: string | null;
  agent_role: string | null;
  message_status: string | null;
  metadata: string | Record<string, unknown> | null;
  created_at: string;
  files: ChatViewFile[];
}

/**
 * Minimal conversation metadata returned by `getChatView`.
 * Only the fields used by the chat route to render the header / gate hub
 * ownership are returned — keeps the RPC payload small while still
 * eliminating a second DO round-trip per chat load.
 */
export interface ChatViewConversation {
  conversation_id: string;
  hub_id: string | null;
  user_id: string | null;
  status: string | null;
  current_responder_type: string | null;
  current_agent_id: string | null;
  current_channel_id: string | null;
  current_channel_type: string | null;
  kanban_status: string | null;
  created_at: string | null;
  updated_at: string | null;
  hub_type: string | null;
  // current_team_user_id is a platform user_id, not a hub_team_user_id.
  current_hub_team_id: string | null;
  current_team_user_id: string | null;
}

/**
 * Composite read response returned by `ConversationDO.getChatView`.
 * Combines messages (with joined files) and conversation metadata in a
 * single DO round-trip, replacing the legacy 2-fetch pattern
 * (v2GetMessages + v2GetConversation).
 */
export interface ChatView {
  conversation: ChatViewConversation | null;
  messages: ChatViewMessage[];
  total: number;
}
